import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout/Layout';
import Modal from '../../../components/Modal/Modal';
import api from '../../../api/axios';
import { FILE_BASE } from '../../../api/config';
import './Trainings.css';

const emptyAnswer = () => ({ answerText: '', isCorrect: false });
const emptyQuestion = () => ({ questionText: '', answers: [emptyAnswer(), emptyAnswer()] });

export default function TrainingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qModalOpen, setQModalOpen] = useState(false);
  const [editQ, setEditQ] = useState(null); // null = new
  const [qForm, setQForm] = useState(emptyQuestion());
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadTraining(); }, [id]);

  async function loadTraining() {
    const res = await api.get(`/trainings/${id}`);
    setTraining(res.data);
    setLoading(false);
  }

  function openNewQuestion() {
    setEditQ(null);
    setQForm(emptyQuestion());
    setQModalOpen(true);
  }

  function openEditQuestion(q) {
    setEditQ(q);
    setQForm({ questionText: q.question_text, answers: q.answers.map(a => ({ answerText: a.answer_text, isCorrect: !!a.is_correct })) });
    setQModalOpen(true);
  }

  async function saveQuestion(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editQ) {
        await api.put(`/trainings/${id}/questions/${editQ.id}`, qForm);
      } else {
        await api.post(`/trainings/${id}/questions`, qForm);
      }
      setQModalOpen(false);
      loadTraining();
    } finally {
      setSaving(false);
    }
  }

  async function deleteQuestion(qId) {
    if (!window.confirm('Frage löschen?')) return;
    await api.delete(`/trainings/${id}/questions/${qId}`);
    loadTraining();
  }

  async function handleDeleteTraining() {
    if (!window.confirm(`Schulung "${training.title}" wirklich löschen?`)) return;
    await api.delete(`/trainings/${id}`);
    navigate('/admin/trainings');
  }

  function updateAnswer(idx, field, value) {
    const answers = [...qForm.answers];
    answers[idx] = { ...answers[idx], [field]: value };
    setQForm({ ...qForm, answers });
  }

  if (loading) return <Layout title="Schulung"><div>Laden...</div></Layout>;

  return (
    <Layout title={training.title}>
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
        <Link to="/admin/trainings" className="btn btn-ghost btn-sm">← Zurück</Link>
        <Link to={`/admin/trainings/${id}/edit`} className="btn btn-outline btn-sm">Bearbeiten</Link>
        <button className="btn btn-danger btn-sm" onClick={handleDeleteTraining}>Löschen</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="card">
          <h3 style={{ marginBottom: '0.75rem', fontSize: '0.9rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-dark)', letterSpacing: '0.05em' }}>Details</h3>
          {training.description && <p style={{ marginBottom: '0.75rem', color: 'var(--gray-dark)' }}>{training.description}</p>}
          <p><strong>Bestehensgrenze:</strong> {training.passing_score}%</p>
          {training.pdf_original_name && (
            <p style={{ marginTop: '0.5rem' }}>
              <strong>PDF:</strong>{' '}
              <a href={`${FILE_BASE}/uploads/pdfs/${training.pdf_path}`} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>
                {training.pdf_original_name}
              </a>
            </p>
          )}
        </div>
        <div className="card">
          <h3 style={{ marginBottom: '0.75rem', fontSize: '0.9rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-dark)', letterSpacing: '0.05em' }}>Quiz</h3>
          <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>{training.questions?.length ?? 0}</p>
          <p style={{ color: 'var(--gray-dark)', fontSize: '0.875rem' }}>Fragen</p>
        </div>
      </div>

      <div className="card">
        <div className="page-header" style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Quiz-Fragen</h2>
          <button className="btn btn-primary btn-sm" onClick={openNewQuestion}>+ Frage hinzufügen</button>
        </div>

        {training.questions?.length === 0 ? (
          <p style={{ color: 'var(--gray-dark)', textAlign: 'center', padding: '2rem' }}>Noch keine Fragen vorhanden.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {training.questions.map((q, qi) => (
              <div key={q.id} style={{ background: 'var(--light)', borderRadius: 10, padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 600 }}>{qi + 1}. {q.question_text}</span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEditQuestion(q)}>Bearbeiten</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteQuestion(q.id)}>Löschen</button>
                  </div>
                </div>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {q.answers.map(a => (
                    <li key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                      <span style={{ color: a.is_correct ? 'var(--success)' : 'var(--gray-dark)' }}>
                        {a.is_correct ? '✓' : '○'}
                      </span>
                      <span style={{ color: a.is_correct ? 'var(--dark)' : 'var(--gray-dark)' }}>{a.answer_text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal isOpen={qModalOpen} onClose={() => setQModalOpen(false)} title={editQ ? 'Frage bearbeiten' : 'Neue Frage'} size="lg">
        <form onSubmit={saveQuestion}>
          <div className="form-group">
            <label>Frage</label>
            <textarea value={qForm.questionText} onChange={e => setQForm({ ...qForm, questionText: e.target.value })} required rows={3} placeholder="Frage eingeben..." />
          </div>
          <label style={{ fontWeight: 600, fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>Antwortmöglichkeiten (✓ = richtig)</label>
          <div className="question-form">
            {qForm.answers.map((a, i) => (
              <div key={i} className="answer-item">
                <input type="checkbox" checked={a.isCorrect} onChange={e => updateAnswer(i, 'isCorrect', e.target.checked)} title="Richtig" />
                <input type="text" value={a.answerText} onChange={e => updateAnswer(i, 'answerText', e.target.value)} placeholder={`Antwort ${i + 1}`} required />
                {qForm.answers.length > 2 && (
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => setQForm({ ...qForm, answers: qForm.answers.filter((_, j) => j !== i) })}>✕</button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: '0.5rem' }}
              onClick={() => setQForm({ ...qForm, answers: [...qForm.answers, emptyAnswer()] })}>
              + Antwort hinzufügen
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQModalOpen(false)}>Abbrechen</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
}
